import { readItemField } from './item-field-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { phpJsonInt, phpJsonString } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { createClassificationContext } from './from-base-classification-context';
import valueTypes from './from-base-monster-value-types.json';

type Context = ReturnType<typeof createClassificationContext>;
const taxonomy = new Set(['creature_type', 'creature_sub_type', 'creature_template']);
const ancestry = (type: unknown) => type === 'creature_ancestry' || type === 'creature_sub_type_ancestral';
const integerTypes = new Set(valueTypes.integer), decimalTypes = new Set(valueTypes.decimal);

/** Native layer_append targets an existing bucket slot, otherwise its legacy
 * mirror. Keep that distinction until the later canonical mirror window. */
function append(row: Row, name: string, value: unknown): void {
    const layers = row.item_layer_types as Row;
    const bucket = Object.prototype.hasOwnProperty.call(layers, name) ? layers : row;
    const previous = bucket[name];
    bucket[name] = previous === undefined ? [value] : Array.isArray(previous) && previous.length
        ? [...previous, value] : [previous, value];
}

/** SELECT monster rows are already sorted by monster_template_layer_i. */
export function applyCreatureMonsterTemplate(source: Row, base: Row, context: Context): Row {
    const { readers, rawDefinition, definition } = context;
    const id = readItemField(fromBaseRecord(source.encounter_creatures) ? source.encounter_creatures : null,
        'encounter_creature_monster_template_id');
    if (!fromBaseUuid(id)) return fromBaseClone(source);
    if (!Array.isArray(base.monster_template_layers)) throw new FromBaseInputMissing('monster-template-layers', id);
    const rows = (base.monster_template_layers as Row[]).filter(row => readItemField(row, 'monster_template_id') === id
        && phpJsonInt(readItemField(row, 'monster_template_layer_deleted')) === 0);
    if (!rows.length) return fromBaseClone(source);
    const row = fromBaseClone(source), layers = row.item_layer_types as Row;
    // Preserve the host's resolved base size before prepending classifications.
    // A size-less injected template compiles with a Tiny fallback, which would
    // otherwise shadow the host's ancestry/provider size during canonicalization.
    // Genuinely authored direct/injected size overrides below still replace it.
    context.canonicalSize(row);
    const grouped = new Map<string, Row[]>();
    for (const entry of rows) {
        const name = phpJsonString(readItemField(entry, 'item_layer_type_name'));
        if (name) grouped.set(name, [...grouped.get(name) ?? [], entry]);
    }
    const ownReaders = context.ownReaders;
    for (const [name, entries] of grouped) {
        if (taxonomy.has(name)) {
            // The native override accepts an array here; a bare single-valued
            // taxonomy UUID is deliberately not promoted to a list.
            const stored = Object.prototype.hasOwnProperty.call(layers, name) ? layers[name] : row[name];
            let existing = stored !== null && typeof stored === 'object' ? Object.values(stored) : [];
            const divisionType = (entry: unknown) => {
                const uid = readLayerListEntryUuid(entry);
                return fromBaseUuid(uid) ? readItemField(rawDefinition(uid), 'item_type') : null;
            };
            if (name === 'creature_sub_type' && entries.some(entry => ancestry(divisionType(readItemField(entry, 'item_layer_effect')))))
                existing = existing.filter(entry => !ancestry(divisionType(entry)));
            const seen = new Set(existing.map(readLayerListEntryUuid).filter(Boolean));
            const prepended: unknown[] = [];
            for (const entry of entries) {
                const effect = readItemField(entry, 'item_layer_effect');
                if (effect === null || effect === '') continue;
                const uid = readLayerListEntryUuid(effect);
                if (uid && seen.has(uid)) continue;
                if (uid) seen.add(uid);
                prepended.push(effect);
                if (!fromBaseUuid(uid)) continue;
                const catalog = fromBaseRecord(row.catalog) ? row.catalog : {};
                if (!catalog[uid]) catalog[uid] = { item_name: readItemField(rawDefinition(uid), 'item_name') ?? '',
                    item_type: name === 'creature_sub_type' ? divisionType(effect) ?? name : name };
                row.catalog = catalog;
            }
            layers[name] = [...prepended, ...existing]; row[name] = fromBaseClone(layers[name]);
        } else {
            // A direct monster override replaces both stores before appending saved rows.
            delete row[name]; delete row[name + '_qty']; delete layers[name]; delete layers[name + '_qty'];
            for (const entry of entries) {
                let effect = readItemField(entry, 'item_layer_effect');
                if (typeof effect === 'string' && /^\d+$/.test(effect) && integerTypes.has(name))
                    effect = phpJsonInt(effect);
                else if (typeof effect === 'string' && isPhpNumeric(effect) && decimalTypes.has(name)) effect = Number(effect);
                append(row, name, effect);
                const qty = readItemField(entry, 'qty');
                if (qty !== null) append(row, name + '_qty', qty);
            }
        }
    }
    const computed = fromBaseRecord(row.computed) ? row.computed : {};
    row.computed = { ...computed, mt: { ...(fromBaseRecord(computed.mt) ? computed.mt : {}),
        direct_loadout_override: Object.fromEntries(['inventory_equipped', 'inventory_unequipped'].map(name => [name, grouped.has(name)])) } };
    const injected = [...new Set((grouped.get('creature_template') ?? []).map(entry => readLayerListEntryUuid(readItemField(entry, 'item_layer_effect'))))]
        .filter(fromBaseUuid).map(id => ({ raw: rawDefinition(id), blob: definition(id) })).filter(entry => entry.blob);
    for (const name of ['size', 'cr', 'v_dice', 'v_die', 'v_mod', 'v_dice_multiplier', 'body', 'mind', 'soul', 'spirit',
        'fortune', 'merchant', 'trainer', 'swarm_width_lanes']) {
        if (grouped.has(name)) continue;
        for (const entry of injected) {
            // A generated Tiny default is not an authored template size.
            if (name === 'size' && ownReaders.layerValue(entry.raw, 'size') === null) continue;
            const value = readers.layerValue(entry.blob, name);
            if (value === null || value === '') continue;
            delete row[name]; delete row[name + '_qty']; delete layers[name]; delete layers[name + '_qty'];
            append(row, name, value); break;
        }
    }
    const loadoutKey = (entry: unknown): string => {
        const uid = readLayerListEntryUuid(entry).trim().toUpperCase();
        const suffix = typeof entry === 'string' ? entry.trim().split('|').slice(1).join('|') : '';
        const template = typeof entry === 'string' ? fromBaseUuid(suffix) ? suffix : ''
            : fromBaseRecord(entry) ? readItemField(entry, 'template_id') ?? '' : '';
        return uid ? uid + '|' + phpJsonString(template).trim().toUpperCase() : '';
    };
    for (const name of ['inventory_equipped', 'inventory_unequipped']) {
        const existing = Object.values(ownReaders.layerEntries(row, name));
        let claimed = existing.length > 0;
        const seen = new Set(existing.map(loadoutKey).filter(Boolean));
        if (grouped.has(name)) continue;
        for (const entry of injected) {
            const equipment = Object.values(readers.layerEntries(entry.blob, name));
            if (!equipment.length) continue;
            // Only the first source is worn. Losing equipped lists are dropped.
            if (name === 'inventory_equipped' && claimed) continue;
            claimed = true;
            for (const item of equipment) {
                const key = loadoutKey(item);
                if (key && seen.has(key)) continue;
                if (key) seen.add(key);
                append(row, name, item);
            }
        }
    }
    return row;
}
